import React from 'react';
import { Card } from "../ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { ActionItemStatus } from '../../types/actionItems';
import { useQuery } from '@tanstack/react-query';
import { actionItemsService } from '../../services/supabase/action-items';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLORS = {
  todo: '#94A3B8',
  in_progress: '#3B82F6',
  on_hold: '#F59E0B',
};

export function DashboardStats() {
  const { user } = useAuth();

  const { data: actionItems = [] } = useQuery({
    queryKey: ['actionItems', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      return actionItemsService.getActionItems(user.id);
    },
    enabled: !!user?.id
  });

  const openStatuses: ActionItemStatus[] = ['todo', 'in_progress', 'on_hold'];
  
  const statusCounts = actionItems.reduce((acc: Record<string, number>, item) => {
    if (openStatuses.includes(item.status)) {
      acc[item.status] = (acc[item.status] || 0) + 1;
    }
    return acc;
  }, {});

  const chartData = openStatuses.map(status => ({
    name: status,
    value: statusCounts[status] || 0,
    label: status === 'todo' ? 'To Do' : 
           status.split('_').map(word => 
             word.charAt(0).toUpperCase() + word.slice(1)
           ).join(' ')
  }));

  const totalOpenItems = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-col gap-2">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-foreground">
              {entry.value}
            </span>
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-xs font-medium">
              {entry.payload.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="bg-slate-200 dark:bg-slate-700/50 border-2 border-slate-300 dark:border-slate-700 rounded-3xl">
      <div className="flex items-center justify-between">
        <div className="relative w-48 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={8}
                cornerRadius={10}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={STATUS_COLORS[entry.name as keyof typeof STATUS_COLORS]}
                    className="stroke-gray-500"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
              <div className="text-3xl font-bold text-foreground">{totalOpenItems}</div>
              <div className="text-xs text-muted-foreground">Open</div>
            
            
            </div>
          </div>
        </div>
        <div className="flex-1 ml-4">
          <CustomLegend 
            payload={chartData.map(item => ({
              value: item.label,
              color: STATUS_COLORS[item.name as keyof typeof STATUS_COLORS],
              payload: item
            }))}
          />
        </div>
      </div>
    </Card>
  );
}